import { PLAN_PHASE, PLAN_PHASE_LABELS, formatSets, type TrainingExerciseDto, type TrainingPlanDto } from '@ukinfo/shared';
import { useQueries } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api';
import { itemExerciseId } from '../../lib/training';
import { ExerciseText } from './common';
import { DiagramLegend, PitchDiagram } from './PitchDiagram';

/** Klasse am body, solange gedruckt wird - das Druck-CSS (index.css) blendet dann alles andere aus. */
export const PRINT_BODY_CLASS = 'druckt-einheit';

/**
 * Druckansicht einer Einheit.
 *
 * Das Blatt haengt direkt am body und ist am Bildschirm unsichtbar. Es laedt
 * die Beschreibungen aller Uebungen nach und meldet mit `onReady`, wenn alles
 * da ist - erst dann wird gedruckt.
 */
export function PlanPrintSheet({ plan, subtitle, onReady }: { plan: TrainingPlanDto; subtitle: string; onReady: () => void }): JSX.Element {
  const ids = [...new Set(plan.items.map(itemExerciseId).filter((id): id is string => id !== null))];
  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['training-exercise', id],
      queryFn: () => api.get<TrainingExerciseDto>(`/api/training/exercises/${id}`),
      staleTime: 60_000,
    })),
  });
  // Auch ein Fehler gilt als erledigt: eine fehlende Beschreibung soll den Druck nicht blockieren.
  const settled = results.every((result) => !result.isPending);
  const details = new Map(ids.map((id, index) => [id, results[index]?.data] as const));

  const fired = useRef(false);
  useEffect(() => {
    if (settled && !fired.current) {
      fired.current = true;
      onReady();
    }
  }, [settled, onReady]);

  return createPortal(
    <div className="plan-print-sheet" data-testid="plan-print-sheet">
      <h1 className="plan-print-title">{plan.title}</h1>
      <p className="plan-print-meta">{subtitle}</p>
      {plan.focus && <p><strong>Schwerpunkt:</strong> {plan.focus}</p>}
      {plan.notes && <p className="plan-print-notes">{plan.notes}</p>}
      <p><strong>Gesamt:</strong> {plan.totalMinutes} Min.</p>

      {PLAN_PHASE.map((phase) => {
        const items = plan.items.filter((item) => item.phase === phase).sort((a, b) => a.position - b.position);
        if (items.length === 0) return null;
        return (
          <section key={phase} className="plan-print-phase">
            <h2>{PLAN_PHASE_LABELS[phase]} · {plan.minutesByPhase[phase]} Min.</h2>
            {items.map((item, index) => {
              const id = itemExerciseId(item);
              const detail = id ? details.get(id) : undefined;
              const exercise = item.exercise;
              return (
                <article key={item.id} className="plan-print-item">
                  <h3>{index + 1}. {item.title} <span>({item.durationMinutes} Min.)</span></h3>
                  {exercise && formatSets(exercise) && <p><strong>Durchgänge:</strong> {formatSets(exercise)}</p>}
                  {item.note && <p className="plan-print-note">{item.note}</p>}
                  <div className="plan-print-body">
                    {exercise?.diagram && (
                      <div className="plan-print-sketch">
                        <PitchDiagram diagram={exercise.diagram} title={item.title} />
                        {/* Nur die Bausteine, die in der Skizze vorkommen - gedruckt gibt es kein Antippen zum Nachsehen. */}
                        <DiagramLegend diagram={exercise.diagram} className="plan-print-legend mt-1" />
                      </div>
                    )}
                    <div className="plan-print-text">
                      {exercise?.isLink && exercise.linkUrl && <p>Übung im Netz: {exercise.linkUrl}</p>}
                      {detail && !detail.isLink && <ExerciseText exercise={detail} compact />}
                      {!detail && exercise?.material && <p><strong>Material:</strong> {exercise.material}</p>}
                      {exercise && exercise.videoCount > 0 && <p><strong>Video vorhanden</strong> (in der Anwendung ansehen)</p>}
                    </div>
                  </div>
                </article>
              );
            })}
          </section>
        );
      })}
    </div>,
    document.body,
  );
}
